// communityDetail/styled.js
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  TextField, 
  FormControl,
  Select,
  Switch,
  Chip,
  Card,
  CardContent,
  Tab,
  Tabs,
  TableCell,
  TableRow,
  Avatar
} from '@mui/material';

// Honey color theme
export const honeyColors = {
  primary: '#F1C40F',
  primaryLight: '#F4D03F',
  primaryDark: '#D4AC0D',
  amber: '#FFB74D',
  cream: '#FFF8E1',
  honeycomb: '#D4A574',
  caramel: '#8D6E63',
  success: '#66BB6A',
  warning: '#FF9800',
  info: '#29B6F6',
  error: '#EF5350',
  background: '#FFFEF7',
  surface: '#FFFFFF',
  surfaceHoney: '#FFF9E6',
  border: '#F5E6B3',
  textPrimary: '#3E2723',
  textSecondary: '#5D4037',
  textMuted: '#8D6E63',
  gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
  gradientHoneycomb: 'linear-gradient(135deg, #D4A574 0%, #F1C40F 50%, #FFB74D 100%)',
  gradientGlass: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
  shadowLight: '0 2px 8px rgba(212, 165, 116, 0.15)',
  shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
  shadowHeavy: '0 8px 32px rgba(212, 165, 116, 0.35)',
  shadowGlow: '0 0 20px rgba(241, 196, 15, 0.3)',
};

export const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: honeyColors.background,
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2),
  },
}));

export const HeaderSection = styled(Box)(({ theme }) => ({
  backgroundColor: honeyColors.surface,
  borderRadius: '24px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: honeyColors.gradientPrimary,
  },
}));

export const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
}));

export const CommunityAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${honeyColors.primary}`,
  boxShadow: honeyColors.shadowMedium,
  [theme.breakpoints.down('md')]: {
    width: 100,
    height: 100,
  },
}));

export const HeaderInfo = styled(Box)(({ theme }) => ({
  flex: 1,
}));

export const CommunityName = styled(Typography)({
  fontSize: '2rem !important',
  fontWeight: '800 !important',
  color: honeyColors.textPrimary,
  marginBottom: '8px !important',
});

export const CommunitySlug = styled(Typography)({
  color: honeyColors.textMuted,
  fontSize: '0.9rem !important',
  fontFamily: 'monospace !important',
  backgroundColor: honeyColors.surfaceHoney,
  padding: '4px 12px',
  borderRadius: '8px',
  display: 'inline-block',
  marginBottom: '12px !important',
});

export const StatusBadge = styled(Chip)(({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'AC': return { bg: honeyColors.success, text: '#fff' };
      case 'PP': return { bg: honeyColors.warning, text: '#fff' };
      case 'IN': return { bg: honeyColors.textMuted, text: '#fff' };
      case 'RE': return { bg: honeyColors.error, text: '#fff' };
      case 'BA': return { bg: '#000', text: '#fff' };
      default: return { bg: honeyColors.border, text: honeyColors.textPrimary };
    }
  };
  
  const colors = getStatusColor();
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

export const VisibilityBadge = styled(Chip)(({ visibility }) => {
  const getVisibilityColor = () => {
    switch (visibility) {
      case 'PU': return { bg: honeyColors.info, text: '#fff' };
      case 'PR': return { bg: honeyColors.amber, text: '#fff' };
      case 'SE': return { bg: honeyColors.caramel, text: '#fff' };
      default: return { bg: honeyColors.border, text: honeyColors.textPrimary };
    }
  };
  
  const colors = getVisibilityColor();
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: 600,
    fontSize: '0.75rem',
  };
});

export const QuickStats = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
  [theme.breakpoints.down('md')]: {
    justifyContent: 'center',
  },
}));

export const QuickStat = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(1.5, 2.5),
  backgroundColor: honeyColors.surfaceHoney,
  borderRadius: '12px',
  border: `1px solid ${honeyColors.border}`,
  minWidth: '100px',
}));

export const StatNumber = styled(Typography)({
  fontSize: '1.5rem !important',
  fontWeight: '700 !important',
  color: honeyColors.primary,
});

export const StatLabel = styled(Typography)({
  fontSize: '0.75rem !important',
  color: honeyColors.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
});

export const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: honeyColors.surface,
  borderRadius: '16px',
  padding: theme.spacing(0.5),
  marginBottom: theme.spacing(3),
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  
  '& .MuiTabs-indicator': {
    backgroundColor: honeyColors.primary,
    height: '4px',
    borderRadius: '4px',
  },
}));

export const StyledTab = styled(Tab)({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  color: honeyColors.textSecondary,
  minHeight: '56px',
  
  '&.Mui-selected': {
    color: honeyColors.textPrimary,
  },
  
  '&:hover': {
    color: honeyColors.primary,
    backgroundColor: honeyColors.surfaceHoney,
  },
});

export const TabPanel = styled(Box)(({ theme }) => ({
  backgroundColor: honeyColors.surface,
  borderRadius: '20px',
  padding: theme.spacing(3),
  border: `2px solid ${honeyColors.border}`,
  boxShadow: honeyColors.shadowLight,
  minHeight: '400px',
}));

export const SectionCard = styled(Card)({
  backgroundColor: honeyColors.surface,
  borderRadius: '16px',
  border: `1px solid ${honeyColors.border}`,
  boxShadow: 'none',
  marginBottom: '16px',
  
  '&:hover': {
    borderColor: honeyColors.primaryLight,
    boxShadow: honeyColors.shadowLight,
  },
});

export const SectionTitle = styled(Typography)({
  fontSize: '1.25rem !important',
  fontWeight: '700 !important',
  color: honeyColors.textPrimary,
  marginBottom: '16px !important',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  
  '& .MuiSvgIcon-root': {
    color: honeyColors.primary,
  },
});

export const FormGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '20px',
});

export const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: honeyColors.surfaceHoney,
    border: `2px solid ${honeyColors.border}`,
    transition: 'all 0.2s ease',
    
    '&:hover': {
      borderColor: honeyColors.primary,
    },
    
    '&.Mui-focused': {
      borderColor: honeyColors.primary,
      boxShadow: honeyColors.shadowGlow,
    },
    
    '& fieldset': {
      border: 'none',
    },
  },
  
  '& .MuiInputLabel-root': {
    color: honeyColors.textSecondary,
    fontWeight: 600,
    
    '&.Mui-focused': {
      color: honeyColors.primary,
    },
  },
});

export const StyledSelect = styled(Select)({
  borderRadius: '12px',
  backgroundColor: honeyColors.surfaceHoney,
  border: `2px solid ${honeyColors.border}`,
  
  '&:hover': {
    borderColor: honeyColors.primary,
  },
  
  '&.Mui-focused': {
    borderColor: honeyColors.primary,
    boxShadow: honeyColors.shadowGlow,
  },
  
  '& fieldset': {
    border: 'none',
  },
});

export const ActionButton = styled(Button)(({ variant }) => ({
  borderRadius: '12px',
  padding: '10px 24px',
  fontWeight: 600,
  textTransform: 'none',
  
  ...(variant === 'contained' && {
    backgroundColor: honeyColors.primary,
    color: honeyColors.textPrimary,
    boxShadow: honeyColors.shadowLight,
    
    '&:hover': {
      backgroundColor: honeyColors.primaryDark,
      boxShadow: honeyColors.shadowMedium,
    },
  }),
  
  ...(variant === 'outlined' && {
    borderColor: honeyColors.border,
    color: honeyColors.textSecondary,
    
    '&:hover': {
      borderColor: honeyColors.primary,
      backgroundColor: honeyColors.surfaceHoney,
    },
  }),
}));

export const DangerButton = styled(Button)({
  borderRadius: '12px',
  padding: '10px 24px',
  fontWeight: 600,
  textTransform: 'none',
  borderColor: honeyColors.error,
  color: honeyColors.error,
  
  '&:hover': {
    backgroundColor: honeyColors.error,
    color: '#fff',
    borderColor: honeyColors.error,
  },
});

export const StyledTableCell = styled(TableCell)({
  borderBottom: `1px solid ${honeyColors.border}`,
  padding: '16px',
  color: honeyColors.textPrimary,
});

export const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: honeyColors.surfaceHoney,
  },
});

export const MemberAvatar = styled(Avatar)({
  width: 40,
  height: 40,
  border: `2px solid ${honeyColors.border}`,
});

export const RoleBadge = styled(Chip)(({ role }) => {
  const getRoleColor = () => {
    switch (role) {
      case 'FO': return { bg: honeyColors.primary, text: honeyColors.textPrimary };
      case 'AD': return { bg: honeyColors.info, text: '#fff' };
      case 'MA': return { bg: honeyColors.success, text: '#fff' };
      case 'PA': return { bg: honeyColors.surfaceHoney, text: honeyColors.textSecondary };
      case 'GU': return { bg: honeyColors.border, text: honeyColors.textMuted };
      default: return { bg: honeyColors.border, text: honeyColors.textPrimary };
    }
  };
  
  const colors = getRoleColor();
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    fontWeight: 600,
    fontSize: '0.7rem',
    height: '24px',
  };
});

export const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  color: honeyColors.textMuted,
}));

export const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '400px',
});

export const SearchBar = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: honeyColors.surface,
    border: `1px solid ${honeyColors.border}`,
    
    '&:hover': {
      borderColor: honeyColors.primary,
    },
    
    '&.Mui-focused': {
      borderColor: honeyColors.primary,
    },
    
    '& fieldset': {
      border: 'none',
    },
  },
});

export const FilterChip = styled(Chip)(({ active }) => ({
  backgroundColor: active ? honeyColors.primary : honeyColors.surfaceHoney,
  color: active ? honeyColors.textPrimary : honeyColors.textSecondary,
  fontWeight: 600,
  cursor: 'pointer',
  
  '&:hover': {
    backgroundColor: active ? honeyColors.primaryDark : honeyColors.border,
  },
}));

export const DataCard = styled(Card)(({ theme }) => ({
  backgroundColor: honeyColors.surface,
  borderRadius: '16px',
  border: `1px solid ${honeyColors.border}`,
  padding: theme.spacing(2),
  transition: 'all 0.2s ease',
  
  '&:hover': {
    borderColor: honeyColors.primary,
    boxShadow: honeyColors.shadowLight,
  },
}));

export const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1, 0),
  borderBottom: `1px solid ${honeyColors.border}`,
  
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const InfoLabel = styled(Typography)({
  color: honeyColors.textSecondary,
  fontSize: '0.875rem !important',
});

export const InfoValue = styled(Typography)({
  color: honeyColors.textPrimary,
  fontSize: '0.875rem !important',
  fontWeight: '600 !important',
});
